export type PluginPlanCardAction =
    | 'current-plan'
    | 'start-subscription'
    | 'upgrade-to-team'
    | 'downgrade'
    | 'renew'
    | 'renew-and-switch';

interface ResolvePluginPlanCardActionInput {
    subscriptionStatus?: string | null;
    currentPlanCode?: string | null;
    targetPlanCode: string;
    currentPlanPrice?: number | null;
    targetPlanPrice: number;
}

const normalize = (value?: string | null) => (value || '').trim().toUpperCase();

export const isPluginSubscriptionRenewable = (status?: string | null) => {
    const normalizedStatus = (status || '').trim().toLowerCase();
    return normalizedStatus === 'canceled' || normalizedStatus === 'pending_cancellation';
};

export const shouldShowPluginScheduledChange = (status?: string | null): boolean => (
    !isPluginSubscriptionRenewable(status)
);

const isActiveStatus = (status?: string | null) => (
    (status || '').trim().toLowerCase() === 'active'
);

export const resolvePluginPlanCardAction = ({
    subscriptionStatus,
    currentPlanCode,
    targetPlanCode,
    currentPlanPrice,
    targetPlanPrice,
}: ResolvePluginPlanCardActionInput): PluginPlanCardAction => {
    const isCurrentPlan = Boolean(currentPlanCode)
        && normalize(currentPlanCode) === normalize(targetPlanCode);
    const isRenewableSubscription = isPluginSubscriptionRenewable(subscriptionStatus);

    if (isRenewableSubscription) {
        if (isCurrentPlan) {
            return 'renew';
        }

        // A subscription that is ending still owns its current plan through
        // currentPeriodEnd. Starting another checkout would create a second
        // purchase (and can end a trial immediately). A lower plan is instead
        // a renewal choice that keeps the current plan for this period and
        // schedules the lower plan for the next one.
        if (currentPlanCode
            && typeof currentPlanPrice === 'number'
            && targetPlanPrice < currentPlanPrice) {
            return 'renew-and-switch';
        }
    }

    if (isCurrentPlan && isActiveStatus(subscriptionStatus)) {
        return 'current-plan';
    }

    if (!isActiveStatus(subscriptionStatus) || !currentPlanCode) {
        return 'start-subscription';
    }

    if (typeof currentPlanPrice === 'number' && targetPlanPrice < currentPlanPrice) {
        return 'downgrade';
    }

    return 'upgrade-to-team';
};
